
import { use, useEffect, useState } from "react";
import { Box, Flex, Text, Heading, Badge } from "@chakra-ui/react";
import {
  AppButton,
  AppTable,
  TableRow,
  TableCell,
  AppModal,
  AppInput,
  AppSelect,
} from "../../components";
import { colors } from "../../theme";
import { PlusIcon, EditIcon, DeleteIcon, ViewIcon, BlockIcon } from "../../utils/svg";
import { Controller, useForm } from "react-hook-form";
import {
  useUsers,
  useActivateUser,
  useBlockUser,
} from "../../services/useQueries";
import { useDispatch } from "react-redux";
import { setLoading } from "../../redux/reducer/utilsSlice";

const roleOptions = [
  { value: "user", label: "User" },
  { value: "admin", label: "Admin" },
  { value: "super_admin", label: "Super Admin" },
];

const UserManagementScreen = () => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userType, setUserType] = useState("active");
  const [actionType, setActionType] = useState(null); // 'block' or 'activate'
  const [page, setPage] = useState(1);

  // API hooks
  const { data: users, isLoading: usersLoading } = useUsers(userType);
  const usersData = users?.users || [];
  const activateUserMutation = useActivateUser();
  const blockUserMutation = useBlockUser();
const dispatch = useDispatch();
useEffect(() => {
    dispatch(setLoading(usersLoading));
  }, [usersLoading]);
  const headers = [
    "Name",
    "Email",
    "Phone",
    "Role",
    "Mall",
    "Status",
    "Actions",
  ];

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  const handleBlockUser = (user) => {
    setSelectedUser(user);
    setActionType("block");
    setIsReasonModalOpen(true);
    reset({ reason: "" });
  };

  const handleActivateUser = (user) => {
    setSelectedUser(user);
    setActionType("activate");
    setIsReasonModalOpen(true);
    reset({ reason: "" });
  };

  const handleReasonSubmit = (data) => {
    if (!selectedUser) return;
    const payload = {
      reason: data.reason,
      actionById: "admin-id", // TODO: Replace with real admin id
    };
    if (actionType === "block") {
      blockUserMutation.mutate({ id: selectedUser.id, data: payload }, {
        onSuccess: () => setIsReasonModalOpen(false),
      });
    } else if (actionType === "activate") {
      activateUserMutation.mutate({ id: selectedUser.id, data: payload }, {
        onSuccess: () => setIsReasonModalOpen(false),
      });
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "super_admin":
        return "purple";
      case "admin":
        return "blue";
      default:
        return "gray";
    }
  };

  const renderRow = (user, index) => (
    <TableRow key={user.id || user._id || index}>
      <TableCell fontWeight="500">{user.name}</TableCell>
      <TableCell>{user.email}</TableCell>
      <TableCell>{user.phoneNumber || user.phone}</TableCell>
      <TableCell>
        <Badge
          colorPalette={getRoleBadgeColor(user.role)}
          px={2}
          py={1}
          borderRadius="full"
          fontSize="xs"
          textTransform="capitalize"
        >
          {user.role?.replace("_", " ")}
        </Badge>
      </TableCell>
      <TableCell>{user.mall?.name || "—"}</TableCell>
      <TableCell>
        <Badge
          colorPalette={userType === "active" ? "green" : "red"}
          px={2}
          py={1}
          borderRadius="full"
          fontSize="xs"
          textTransform="capitalize"
        >
          {userType === "active" ? "Active" : "Blocked"}
        </Badge>
      </TableCell>
      <TableCell>
        <Flex gap={2}>
          <AppButton
            variant="ghost"
            size="sm"
            onClick={() => handleViewUser(user)}
            leftIcon={<ViewIcon boxSize={4} />}
          />
          {userType === "active" ? (
            <AppButton
              variant="ghost"
              size="sm"
              onClick={() => handleBlockUser(user)}
              leftIcon={<BlockIcon boxSize={4} color="#EF4444" />}
            />
          ) : (
            <AppButton
              variant="ghost"
              size="sm"
              onClick={() => handleActivateUser(user)}
              leftIcon={<EditIcon boxSize={4} color="#22C55E" />}
            />
          )}
        </Flex>
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      {/* Page Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color={colors.text} mb={1}>
            User Management
          </Heading>
          <Text color={colors.textSecondary}>
            Manage all users in the system
          </Text>
        </Box>
        <Flex gap={2}>
          <AppButton
            variant={userType === "active" ? "primary" : "secondary"}
            onClick={() => setUserType("active")}
          >
            Active Users
          </AppButton>
          <AppButton
            variant={userType === "blocked" ? "primary" : "secondary"}
            onClick={() => setUserType("blocked")}
          >
            Blocked Users
          </AppButton>
        </Flex>
      </Flex>

      {/* Table */}
      <AppTable
        headers={headers}
        data={usersData}
        renderRow={renderRow}
        loading={usersLoading}
        pagination={{
          page,
          totalPages: 1, // Update with real pagination if available
          onPageChange: setPage,
        }}
      />

      {/* View Modal */}
      <AppModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="User Details"
        size="md"
        footer={
          <Flex gap={3} justify="flex-end" w="100%">
            <AppButton
              variant="secondary"
              onClick={() => setIsViewModalOpen(false)}
            >
              Close
            </AppButton>
          </Flex>
        }
      >
        {selectedUser && (
          <Flex direction="column" gap={3}>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Name
              </Text>
              <Text color={colors.textSecondary}>{selectedUser.name}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Email
              </Text>
              <Text color={colors.textSecondary}>{selectedUser.email}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Phone
              </Text>
              <Text color={colors.textSecondary}>{selectedUser.phoneNumber || selectedUser.phone}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Role
              </Text>
              <Text color={colors.textSecondary}>{selectedUser.role}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Status
              </Text>
              <Text color={colors.textSecondary}>{userType === "active" ? "Active" : "Blocked"}</Text>
            </Box>
          </Flex>
        )}
      </AppModal>

      {/* Block/Activate Reason Modal */}
      <AppModal
        isOpen={isReasonModalOpen}
        onClose={() => setIsReasonModalOpen(false)}
        title={actionType === "block" ? "Block User" : "Activate User"}
        size="md"
        footer={
          <Flex gap={3} justify="flex-end" w="100%">
            <AppButton
              variant="secondary"
              onClick={() => setIsReasonModalOpen(false)}
            >
              Cancel
            </AppButton>
            <AppButton
              variant="primary"
              onClick={handleSubmit(handleReasonSubmit)}
              isLoading={
                blockUserMutation.isPending || activateUserMutation.isPending
              }
            >
              {actionType === "block" ? "Block User" : "Activate User"}
            </AppButton>
          </Flex>
        }
      >
        <Flex direction="column" gap={4}>
          <AppInput
            label="Reason"
            name="reason"
            placeholder="Enter reason for this action"
            register={register}
            errors={errors}
            required
          />
        </Flex>
      </AppModal>
    </Box>
  );
};

export default UserManagementScreen;
